<script>
import FormGroup from '@maison/components/form/FormGroup.vue';
import TextField from '@maison/components/form/fields/TextField.vue';
import PasswordField from '@maison/components/form/fields/PasswordField';

import LoginFormWithGemini from '@phoenix/mixins/components/form/LoginFormWithGemini';
import { defineAsyncComponent } from 'vue';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';

export default {
  components: {
    FormGroup,
    PhoenixForm: defineAsyncComponent(() => import('@maison/components/form/PhoenixForm.vue')),
    TextField,
    PasswordField,
    PhoneTokenField: defineAsyncComponent(() => import('@maison/components/form/fields/PhoneTokenField')),
    SendPhoneTokenForm: defineAsyncComponent(() => import('@maison/components/myAccount/SendPhoneTokenForm')),
    PhxsgButton,
  },

  mixins: [LoginFormWithGemini],

  computed: {
    getPasswordHelper() {
      return '';
    },
  },

  methods: {
    getRetrievePasswordLinkClass() {
      return 'btn btn--underline btn--fade';
    },
  },
};
</script>

<template>
  <div>
    <send-phone-token-form
      v-if="showTokenOrPasswordButtons"
      ref="sendPhoneTokenForm"
      :mobile-phone="fields.emailOrPhone.value"
      :show-errors-in-toast="shouldShowErrorInToast"
      :should-display-chinese-pipl-statement="true"
      type="login"
      @on-success="sendTokenSuccess"
      @on-fail="sendTokenFail"
    >
      <div class="grid grid-cols-12 gap-4">
        <form-group :attrs="fields.emailOrPhone" class="col-span-12" data-cy="login-form-email">
          <template #default>
            <text-field v-model="fields.emailOrPhone.value" :should-validate="false" />
          </template>

          <template v-if="fields.emailOrPhone.disabled" #help>
            <div class="form-helper-text form-field__helper-text">
              <PhxsgButton variant="fade" underline @click.prevent.stop="back">
                {{ $t('MyAccount.goBack') }}
              </PhxsgButton>
            </div>
          </template>
        </form-group>
      </div>

      <template #form-actions>
        <PhxsgButton
          ref="sendTokenPhoneButton"
          variant="plain"
          color="primary"
          data-cy="use-token-btn"
          @click.prevent="sendToken"
        >
          {{ $t('MyAccount.sendPhoneToken') }}
        </PhxsgButton>

        <PhxsgButton variant="plain" color="primary" data-cy="use-password-btn" @click.prevent.stop="usePassword">
          {{ $t('MyAccount.usePassword') }}
        </PhxsgButton>
      </template>
    </send-phone-token-form>

    <phoenix-form
      v-else
      ref="phoenixForm"
      v-model="fields"
      :action="formAction"
      :prefill-enabled="true"
      :redirect-url="redirectUrl"
      :show-errors-in-toast="shouldShowErrorInToast"
      :should-display-submit-button="showLoginButton"
      :custom-submit-text="$t('MyAccount.login')"
      :on-success-callback="addTrackingInformation"
      data-cy="main-login-form"
    >
      <div class="grid grid-cols-12 gap-4">
        <form-group :attrs="fields.emailOrPhone" class="col-span-12" data-cy="login-form-email">
          <template #default>
            <text-field v-model="fields.emailOrPhone.value" />
          </template>

          <template v-if="fields.emailOrPhone.disabled" #help>
            <div class="form-helper-text form-field__helper-text">
              <PhxsgButton underline variant="fade" @click.prevent.stop="back">
                {{ $t('MyAccount.goBack') }}
              </PhxsgButton>
            </div>
          </template>
        </form-group>

        <form-group v-if="showPasswordField" :attrs="fields.password" class="col-span-12" data-cy="login-form-password">
          <password-field v-model="fields.password.value" />
        </form-group>

        <template v-if="showTokenField">
          <p v-if="hasPhoneTokenTooManyRetries">
            {{ $t('Authentication.phone.token.retryIn', { timeLeft: timeLeftBeforeTokenRetry }) }}
          </p>

          <phone-token-field
            v-else
            v-model="fields.phoneToken.value"
            :attrs="fields.phoneToken"
            class="col-span-12"
            data-cy="login-form-token"
            @resend-phone-token="backToPhoneStep"
          />
        </template>

        <div v-else class="col-span-12">
          <a :href="getRetrievePasswordUrl()" class="btn btn--underline btn--fade">
            <span class="btn__text">{{ $t('MyAccount.forgotYourPassword') }}</span>
          </a>
        </div>
      </div>

      <template #form-actions="{ isDisabled }">
        <PhxsgButton
          v-if="showNextButton"
          variant="plain"
          color="primary"
          :disabled="isNextButtonDisabled || isDisabled"
          data-cy="next-btn"
          @click.prevent.stop="next"
        >
          {{ $t('MyAccount.loginNext') }}
        </PhxsgButton>
      </template>
    </phoenix-form>
  </div>
</template>
